import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  GridRegistrationModel,
  PlaceInServiceRequestForm
} from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ComparisonTableData,
  ComparisonTableHeader
} from '@bo-client/shared/components/comparison-table/comparison-table.component';
import {
  MastrDataDto,
  MastrLocationDto,
  MastrPvPlantWithLocationDto
} from '@bo-client/activation-management/shared/components/mastr-validation/MastrData.dto';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  tap,
  switchMap,
  combineLatestWith,
  shareReplay
} from 'rxjs/operators';
import { Observe } from '@bo-client/shared/observe.util';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { NBPConfig } from '@bo-client/shared/config.model';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';

@Component({
  selector: 'app-mastr-validation',
  templateUrl: './mastr-validation.component.html',
  styleUrl: './mastr-validation.component.scss'
})
export class MastrValidationComponent implements OnInit {
  /** Datensatz der Netzanmeldung */
  @Input() grData: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value'];
  @Observe('grData') grData$!: Observable<
    GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']
  >;
  /** Nummer im Marktstammdatenregister */
  @Input() mastrNumber: string;
  @Observe('mastrNumber') mastrNumber$: Observable<string>;

  @Output() mastrDataMismatchFields = new EventEmitter<string[]>();
  @Output() mastrDataFetch = new EventEmitter<MastrPvPlantWithLocationDto>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly httpHapService = inject(HttpHapService);
  private readonly dataShareService = inject(DataShareService);

  /** Funktionen, zum Mappen der Netzanmeldungsdaten,
   * um sie in das Format vom Marktstammdatenregister zu bekommen*/
  private readonly dataMapUtil = {
    gridRegistrationModel: {
      getGrModuleNumber(
        data: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']
      ) {
        return data.pvModules.map((item) => item.count).reduce((acc, cur) => acc + cur, 0);
      },
      getInstalledPower(
        data: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']
      ) {
        return data.producers
          .map((item) => item.count * item.acActivePowerMax)
          .reduce((acc, cur) => acc + cur, 0);
      },
      getBruttoPower(data: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']) {
        return (
          data.pvModules.map((item) => item.count * item.modulePowerWatt).reduce((acc, cur) => acc + cur, 0) /
          1000
        );
      },
      getCustomerData: (
        key: keyof (
          | GridRegistrationModel
          | FeederContractModel
          | PlaceInServiceRequestForm['value']
        )['customer']
      ) => {
        return (data: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']) =>
          (data.customer || {})[key];
      }
    },
    mastrDataModel: {
      getLocationData: (key: keyof MastrLocationDto) => {
        return (data: MastrDataDto) => (data.location || {})[key];
      }
    }
  };
  /** Gibt ein Schema für die Daten an, die in der Vergleichstabelle angezeigt werden sollen.
   *
   * Enthält das Label der Datensätze sowie die Getter-Funktionen oder Schlüssel,
   * um die Daten aus dem GridRegistrationModel und dem MastrDataDto zu extrahieren.
   */
  private readonly dataGetters: DataGetter[] = [
    {
      label: 'AC-maximale Wirkleistung (gesamt)',
      formFieldName: 'newProduction',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getInstalledPower,
      mastrDataGetter: 'production'
    },
    {
      label: 'Anzahl der Module',
      formFieldName: 'pvModules',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getGrModuleNumber,
      mastrDataGetter: 'numberOfModules'
    },
    {
      label: 'Gesamtleistung der PV-Module',
      formFieldName: 'newInstalledPower',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getBruttoPower,
      mastrDataGetter: 'installedPower'
    },
    {
      label: 'PLZ Anlagenbetreiber',
      formFieldName: 'customer.zipcode',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getCustomerData('zipCode'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getLocationData('postleitzahl'),
      warnOnly: true
    },
    {
      label: 'Ort Anlagenbetreiber',
      formFieldName: 'customer.city',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getCustomerData('city'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getLocationData('ort'),
      warnOnly: true
    },
    {
      label: 'Straße Anlagenbetreiber',
      formFieldName: 'customer.street',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getCustomerData('street'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getLocationData('strasse'),
      warnOnly: true
    },
    {
      label: 'HausNr Anlagenbetreiber',
      formFieldName: 'customer.streetNo',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getCustomerData('streetNo'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getLocationData('hausnummerWert'),
      warnOnly: true
    }
  ];
  readonly tableHeader: ComparisonTableHeader = {
    labelHeader: 'Beschreibung',
    firstDataHeader: 'Angabe im Portal',
    secondDataHeader: 'Angabe im Marktstammdatenregister'
  };
  _data$: BehaviorSubject<ComparisonTableData[]> = new BehaviorSubject([]);
  data$ = this._data$.asObservable();

  private _error$ = new BehaviorSubject<{ title: string; message: string } | null>(null);
  error$ = this._error$.asObservable();

  private emptyComparisonData(): ComparisonTableData[] {
    return [
      {
        label: 'Keine Daten verfügbar',
        firstDataPoint: '—',
        secondDataPoint: '—'
      }
    ];
  }

  private mapToComparisonData(
    mastr: MastrDataDto,
    model: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value'],
    config: NBPConfig
  ): ComparisonTableData[] {
    return this.dataGetters.map((getter) => {
      let allowedDeviation: number;
      switch (getter.formFieldName) {
        case 'newInstalledPower': {
          const deviation = config.gridRegistration?.mastrValidationConfig?.allowedPowerDeviation.replace(
            ',',
            '.'
          );
          allowedDeviation = Number(deviation);
          break;
        }
        case 'newProduction': {
          const deviation = config.gridRegistration?.mastrValidationConfig?.allowedPowerDeviation.replace(
            ',',
            '.'
          );
          allowedDeviation = Number(deviation);
          break;
        }
        case 'pvModules': {
          const deviation =
            // Regex prüft auf Komma und Punkt als Dezimaltrennzeichen
            config.gridRegistration?.mastrValidationConfig?.allowedModuleCountDeviation.split(/[,.]/)[0];
          allowedDeviation = Number(deviation);
          break;
        }
        default: {
          break;
        }
      }
      return {
        label: getter.label,
        warnOnly: getter.warnOnly,
        allowedDeviation: allowedDeviation || undefined,
        firstDataPoint:
          typeof getter.grDataGetter === 'function'
            ? getter.grDataGetter.call(this, model)
            : model[getter.grDataGetter],
        secondDataPoint:
          typeof getter.mastrDataGetter === 'function'
            ? getter.mastrDataGetter.call(this, mastr)
            : mastr[getter.mastrDataGetter]
      };
    });
  }

  ngOnInit(): void {
    this.mapData();
  }

  private mapData() {
    this.mastrNumber$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        tap(() => {
          this.mastrDataMismatchFields.emit([]); // Array leeren, damit alte Hinweistexte nicht mehr angezeigt werden
          this._data$.next([]); // Daten zurücksetzen, damit beim Laden der neuen Daten die Tabelle leer ist
          this._error$.next(null); // Fehlermeldung zurücksetzen, damit alte Errortexte nicht mehr angezeigt werden
        }),
        filter(this.checkValidity),
        switchMap((mastrNr) =>
          this.httpHapService
            .getTyped<MastrPvPlantWithLocationDto>(`/external/mastr-data/${mastrNr}`) // <-- neue hierarchische Struktur!
            .pipe(
              tap((response) => {
                this.mastrDataFetch.emit(response);
              }),
              combineLatestWith(this.grData$, this.dataShareService.tenantConfig$),
              map(([pvPlant, grData, config]) =>
                this.mapToComparisonData(new MastrDataDto(pvPlant), grData, config)
              ), // Wrapper für Kompatibilität
              catchError((err) => {
                // eslint-disable-next-line no-console
                console.error('Fehler beim Mastr‑Abruf', err);
                this._error$.next({
                  title: 'Fehler beim Abrufen der Daten',
                  message: 'Die Daten konnten nicht vom Marktstammdatenregister abgerufen werden.'
                });
                return of(this.emptyComparisonData()); // Stream bleibt lebendig
              })
            )
        ),
        // **alle übrigen** Fehler abfangen (z. Tippfehler in mapToComparisonData)
        catchError((err) => {
          // eslint-disable-next-line no-console
          console.error('Unerwarteter Fehler in der Pipeline', err);
          this._error$.next({
            title: 'Unbekannter Fehler',
            message: 'Es ist ein unerwarteter Fehler aufgetreten.'
          });
          return of(this.emptyComparisonData());
        }),
        shareReplay(1)
      )
      .subscribe((data) => {
        this._data$.next(data);
      });
  }

  handleFieldMismatch(labels: string[]): void {
    const fieldNames = labels.map((label) => {
      const getter = this.dataGetters.find((g) => g.label === label);
      if (!getter) {
        // eslint-disable-next-line no-console
        console.warn(`Kein Getter für Label "${label}" gefunden.`);
        return label; // Fallback auf das Label selbst
      }
      if (getter.formFieldName) return getter.formFieldName;
      if (typeof getter.grDataGetter === 'string') return getter.grDataGetter;
      return label;
    });
    this.mastrDataMismatchFields.emit(fieldNames);
  }

  checkValidity = (mastrNumber: string): boolean => {
    // machen wir erstmal sehr einfach, der Regex hatte nicht gezündet
    // man könnt uach noch die Prüfziffer machen
    if (!mastrNumber?.length) {
      this._error$.next({
        title: 'Keine Marktstammdatenregister-Nummer angegeben',
        message:
          'Bitte geben Sie eine gültige Marktstammdatenregister-Nummer ein, um eine Datenvalidierung zu erhalten'
      });
      return false;
    }
    // Todo: Regex für die Marktstammdatenregister-Nummer
    if (mastrNumber.length != 15) {
      this._error$.next({
        title: 'Ungültige Marktstammdatenregister-Nummer',
        message:
          'Die eingegebene Marktstammdatenregister-Nummer entspricht keinem gültigen Format. Bitte überprüfen Sie die Eingabe.'
      });
      return false;
    }
    return true;
  };
}

type DataGetter =
  | {
      label: string;
      formFieldName?: string; // Optional, falls der Getter nicht dem Formfeldnamen entspricht
      grDataGetter: keyof (GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']);
      mastrDataGetter: keyof MastrDataDto | ((data: MastrDataDto) => unknown);
      allowedDeviation?: number;
      warnOnly?: boolean;
    }
  | {
      label: string;
      formFieldName: string; // Um einen Errortext zu generieren, wenn die Daten nicht übereinstimmen
      grDataGetter: (
        data: GridRegistrationModel | FeederContractModel | PlaceInServiceRequestForm['value']
      ) => unknown;
      mastrDataGetter: keyof MastrDataDto | ((data: MastrDataDto) => unknown);
      allowedDeviation?: number;
      warnOnly?: boolean;
    };
